import React, { useEffect, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import { Form, FloatingLabel } from "react-bootstrap";
import { AshaBlockServices } from "../../../services/AshaBlocks";
import LoadingModal from "../../components/LoadingModal.js";
import AlertModal from "../../../components/AlertModal.js";
import styles from "../../../styles/Modal.module.css";
import { useNavigate } from "react-router-dom";

function AddAshaBlock(props) {
  const { taluks = [], phcs = [] } = props;

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ashaBlocks, setAshaBlocks] = useState({
    ashaBlock: "",
    phcId: "",
    talukId: "",
  });

  const [filteredPhcs, setFilteredPhcs] = useState([]);

  const [error, setError] = useState({
    ashaBlock: "",
    phcId: "",
    talukId: "",
  });

  const [alertData, setAlertData] = useState({
    show: false,
    title: "",
    message: "",
    variant: "success",
  });

  useEffect(() => {
    if (ashaBlocks.talukId) {
      const phcsForTaluk = phcs.filter(
        (p) => String(p.talukId) === String(ashaBlocks.talukId)
      );
      setFilteredPhcs(phcsForTaluk);
    } else {
      setFilteredPhcs([]);
    }
  }, [ashaBlocks.talukId, phcs]);

useEffect(() => {
  if (!props.show) {
    // reset form when modal is closed
    setAshaBlocks({ ashaBlock: "", phcId: "", talukId: "" });
    setError({ ashaBlock: "", phcId: "", talukId: "" });
  }
}, [props.show]);

  const validation = () => {
    let tempError = { ashaBlock: "", phcId: "", talukId: "" };
    let valid = true;
    if (!ashaBlocks.ashaBlock.trim()) {
      tempError.ashaBlock = "Asha Block is required";
      valid = false;
    }
    if (!ashaBlocks.phcId) {
      tempError.phcId = "PHC is required";
      valid = false;
    }
    if (!ashaBlocks.talukId) {
      tempError.talukId = "Taluk is required";
      valid = false;
    }
    setError(tempError);
    return valid;
  };

  const addAshaBlock = async () => {
    if (!validation()) return;

    setLoading(true);
    try {
      const res = await AshaBlockServices.addAshaBlock(ashaBlocks);
      if (res.status === 200) {
        setAlertData({
          show: true,
          title: "Success",
          message: res.message || "Asha Block added successfully!",
          variant: "success",
        });
        setAshaBlocks({ ashaBlock: "", phcId: "", talukId: "" });
        props.onClose && props.onClose();
      } else {
        throw new Error(res.message || "Error while adding Asha Block");
      }
    } catch (error) {
      setAlertData({
        show: true,
        title: "Error",
        message: error.message || "Failed to add Asha Block. Please try again.",
        variant: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAlertClose = () => {
    setAlertData((prev) => ({ ...prev, show: false }));
    if (alertData.variant === "success") {
      props.onHide();
      navigate("/asha-block");
    }
  };

  return (
    <>
      <Modal {...props} size="md" centered className={styles.modalShow}>
        <Modal.Header closeButton className={styles.modalHeader}>
          <Modal.Title className={styles.modalTitle}>
            <i className="fas fa-plus-circle me-2"></i>
            Add New Asha Block
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalBody}>
          {/* Taluk Dropdown */}
          <div className={styles.formGroup}>
            <FloatingLabel controlId="Taluk" label="Taluk*">
              <Form.Select
                value={ashaBlocks.talukId}
                onChange={(e) =>
                  setAshaBlocks({
                    ...ashaBlocks,
                    talukId: e.target.value,
                    phcId: "", // reset PHC when taluk changes
                  })
                }
                isInvalid={!!error.talukId}
              >
                <option value="">Select Taluk</option>
                {taluks.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.taluk}
                  </option>
                ))}
              </Form.Select>
              {error.talukId && (
                <div className="invalid-feedback d-block">{error.talukId}</div>
              )}
            </FloatingLabel>
          </div>

          {/* PHC Dropdown */}
          <div className={styles.formGroup}>
            <FloatingLabel controlId="phc" label="PHC*">
              <Form.Select
                value={ashaBlocks.phcId}
                onChange={(e) =>
                  setAshaBlocks({ ...ashaBlocks, phcId: e.target.value })
                }
                isInvalid={!!error.phcId}
                disabled={!ashaBlocks.talukId}
              >
                <option value="">Select PHC</option>
                {filteredPhcs.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.phc}
                  </option>
                ))}
              </Form.Select>
              {error.phcId && (
                <div className="invalid-feedback d-block">{error.phcId}</div>
              )}
            </FloatingLabel>
          </div>

          {/* Asha Block Input */}
          <div className={styles.formGroup}>
            <FloatingLabel controlId="ashaBlock" label="Asha Block*">
              <Form.Control
                type="text"
                placeholder="Enter Asha Block"
                value={ashaBlocks.ashaBlock}
                onChange={(e) =>
                  setAshaBlocks({ ...ashaBlocks, ashaBlock: e.target.value })
                }
                isInvalid={!!error.ashaBlock}
              />
              <Form.Control.Feedback type="invalid">
                {error.ashaBlock}
              </Form.Control.Feedback>
            </FloatingLabel>
          </div>
        </Modal.Body>
        <Modal.Footer className={styles.modalFooter}>
          <Button
            variant="success"
            onClick={addAshaBlock}
            className={styles.primaryButton}
            disabled={loading}
          >
            {loading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fas fa-save me-1"></i>
                Save Asha Block
              </>
            )}
          </Button>
          <Button
            variant="danger"
            onClick={props.onHide}
            className={styles.secondaryButton}
          >
            <i className="fas fa-times me-1"></i>
            Cancel
          </Button>
        </Modal.Footer>
      </Modal>

      <LoadingModal show={loading} />

      <AlertModal
        show={alertData.show}
        onHide={handleAlertClose}
        title={alertData.title}
        message={alertData.message}
        variant={alertData.variant}
      />
    </>
  );
}

export default AddAshaBlock;
